import {
    type FC,
    type ReactNode,
    type RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import TetherComponent from "react-tether";
import { useMediaQuery } from "@onshopfront/components";
import { type Override } from "../../../types/internal/utility";
import { DIALOG_CLICK } from "../../Actions/Dialogs/DialogActions";
import UUID from "../../Utilities/Generic/UUID";
import TouchNumbers, { type TouchNumbersProps } from "./TouchNumbers";

export const TOUCH_NUMBERS_OPEN = "TOUCH_NUMBERS_OPEN";

interface Props extends Override<TouchNumbersProps, {
    onConfirm?: (value: string) => void;
}> {
    onClose?: () => void;
    prepareOpen: (onOpen: null | ((open: boolean) => void)) => void;
    children: (ref?: RefObject<Element>) => ReactNode;
    ignoreForMobile?: boolean;
    immediateOpen?: boolean;
}

export const TouchNumbersWrapper: FC<Props> = ({
    children,
    prepareOpen,
    onClose,
    ignoreForMobile,
    ...props
}) => {
    const id = useRef("");
    const [ open, setOpen ] = useState(props.immediateOpen ?? false);
    const isMobile = useMediaQuery("(max-width: 700px)");

    const onChange = useCallback((shouldOpen: boolean) => {
        if(shouldOpen) {
            window.dispatchEvent(new CustomEvent(TOUCH_NUMBERS_OPEN, {
                detail: id.current,
            }));
        }

        setOpen(shouldOpen);
    }, []);

    useEffect(() => {
        id.current = `touch-${UUID.generate()}`;

        const clickEvent = (target: HTMLElement) => {
            if(target.closest(`.${id.current}`)) {
                return;
            }

            onClose?.();
            setOpen(false);
        };

        const windowClick = (e: MouseEvent) => {
            clickEvent(e.target as HTMLElement);
        };

        const dialogClick = (e: CustomEvent) => {
            clickEvent(e.detail);
        };

        const openEvent = (e: CustomEvent) => {
            if(id === e.detail) {
                return;
            }

            onClose?.();
            setOpen(false);
        };

        prepareOpen?.(onChange);

        window.addEventListener("click", windowClick);
        window.addEventListener(DIALOG_CLICK, dialogClick);
        window.addEventListener(TOUCH_NUMBERS_OPEN, openEvent);

        return () => {
            window.removeEventListener("click", windowClick);
            window.removeEventListener(DIALOG_CLICK, dialogClick);
            window.removeEventListener(TOUCH_NUMBERS_OPEN, openEvent);
        };
    }, [ prepareOpen, id, onClose, onChange ]);

    if(ignoreForMobile && isMobile) {
        return (<>{children()}</>);
    }

    return (
        <div className={"touch-numbers-event-net " + id.current}>
            <TetherComponent
                attachment="top right"
                targetAttachment="top left"
                constraints={[{
                    to        : "window",
                    attachment: "together",
                    pin       : true,
                }]}
                renderTarget={children}
                renderElement={
                    (ref: RefObject<HTMLDivElement>): ReactNode =>
                        open && (
                            <div
                                ref={ref}
                                className={"touch-numbers-wrapper " + id.current}
                            >
                                <TouchNumbers
                                    {...props}
                                    onConfirm={(value: string) => {
                                        props.onConfirm?.(value);
                                        setOpen(false);
                                    }}
                                />
                            </div>
                        )
                }
            />
        </div>
    );
};
